import React, { useState } from 'react';

import {
  BuildOutlined,
  DashboardOutlined,
  EnvironmentOutlined,
  MonitorOutlined,
  ProfileOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  SkinOutlined,
  StockOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';

import { demandPatternOptions, demandPredictabilityOptions } from 'pages/purchasing/PlanningPage/utils';

import { AsyncMultiSelect } from 'components/AsyncSelect/AsyncMultiSelect';
import { convertToMultiSelectProps } from 'components/AsyncSelect/useAsyncMultiSelectProps';
import {
  useLocationsSelectProps,
  useItemGroupsSelectProps,
  useSuppliersSelectProps,
  useItemsSelectPropsApi,
  usePurchaseClassSelectProps,
  usePurchasingBuyersSelectProps,
  useRulesSelectProps,
  useItemLocationsSelectProps,
} from 'components/AsyncSelect/useAsyncSelectProps';
import { ActionButton } from 'components/Button/ActionButton';
import { FlexSpace } from 'components/FlexSpace';
import { ProductGroupMultiSelect } from 'components/recipes/select/ProductGroupMultiSelect';

import { toSnakeCase } from 'utils/formatting';

import { InventoryDashboardHashStateFilters } from 'types/hash-state';

export const InventoryDashboardFilters = ({
  label,
  filterState,
  onFiltersChange,
  showItemFilter = false,
  showLocationFilter = false,
  showStockableFilter = false,
  showPurchaseClassFilter = false,
  showProductGroupFilter = false,
  showStatusFilter = false,
  showDemandPatternFilter = false,
  showDemandPredictabilityFilter = false,
  showBuyerFilter = false,
  showRulesFilter = false,
  showItemLocationsFilter = false,
  itemLocations,
}: {
  label?: string | React.ReactNode;
  filterState?: InventoryDashboardHashStateFilters;
  onFiltersChange: (filters: InventoryDashboardHashStateFilters) => void;
  showItemFilter?: boolean;
  showLocationFilter?: boolean;
  showStatusFilter?: boolean;
  showStockableFilter?: boolean;
  showPurchaseClassFilter?: boolean;
  showProductGroupFilter?: boolean;
  showDemandPatternFilter?: boolean;
  showDemandPredictabilityFilter?: boolean;
  showBuyerFilter?: boolean;
  showRulesFilter?: boolean;
  showItemLocationsFilter?: boolean;
  itemLocations?: SearchPlanningDTO[];
}) => {
  // Keep track of filters that are open so we only retrieve the data once
  const [openedFilters, setOpenedFilters] = useState<Record<string, boolean>>({});
  const onFilterOpen = (key: string) => (isOpen: boolean) => {
    if (isOpen) setOpenedFilters((prev) => ({ ...prev, [key]: true }));
  };

  const locationsSelectProps = useLocationsSelectProps({ enabled: openedFilters.location ?? false });
  const itemGroupsSelectProps = useItemGroupsSelectProps({ enabled: openedFilters.itemGroups ?? false });
  const supplierSelectProps = useSuppliersSelectProps(undefined, openedFilters.supplier ?? false);
  const purchaseClassSelectProps = usePurchaseClassSelectProps(openedFilters.purchaseClass ?? false);
  const itemsSelectProps = useItemsSelectPropsApi(openedFilters.items ?? false);
  const buyerSelectProps = usePurchasingBuyersSelectProps(openedFilters.buyer ?? false);
  const rulesSelectProps = useRulesSelectProps(openedFilters.rules ?? false);
  const itemLocationsSelectProps = useItemLocationsSelectProps(itemLocations || []);

  const numberOfFiltersApplied = Object.values(filterState ?? {})
    .map((values) => (Array.isArray(values) ? values.length : 0))
    .reduce((a: number, b: number) => a + b, 0);

  const onHashStateFilterChange = (filterKey: keyof InventoryDashboardHashStateFilters, values: unknown) => {
    const filters = { ...filterState };
    if (filterKey !== 'timeSpan' && filterKey !== 'backordered') {
      filters[filterKey] = values as string[];
    }

    onFiltersChange(filters);
  };

  return (
    <FlexSpace gap={8} wrap>
      {label ?? <div>{label}</div>}

      {showItemLocationsFilter ? (
        <AsyncMultiSelect
          selectProps={itemLocationsSelectProps}
          label="Item Locations"
          queryPlaceholder="Search item locations"
          selectedValues={filterState?.itemLocationIds ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('itemLocationIds', values)}
          icon={<SafetyCertificateOutlined />}
        />
      ) : null}

      {showStatusFilter ? (
        <AsyncMultiSelect
          selectProps={{
            options: [
              {
                label: 'Normal',
                value: 'Normal',
              },
              {
                label: 'Overstock',
                value: 'Overstock',
              },
              {
                label: 'Dead Stock',
                value: 'Deadstock',
              },
            ],
          }}
          label="Status"
          selectedValues={filterState?.status ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('status', values)}
          icon={<DashboardOutlined />}
        />
      ) : null}

      {showLocationFilter && (
        <AsyncMultiSelect
          selectProps={convertToMultiSelectProps(locationsSelectProps)}
          label="Location"
          queryPlaceholder="Search locations"
          selectedValues={filterState?.locationIds ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('locationIds', values)}
          onOpenChange={onFilterOpen('location')}
          icon={<EnvironmentOutlined />}
        />
      )}
      {showStockableFilter && (
        <AsyncMultiSelect
          selectProps={{
            options: [
              {
                label: 'Stockable',
                value: 'true',
              },
              {
                label: 'Non-Stockable',
                value: 'false',
              },
            ],
          }}
          label="Stockable"
          selectedValues={filterState?.stockable ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('stockable', values)}
          icon={<BuildOutlined />}
        />
      )}
      {showPurchaseClassFilter && (
        <AsyncMultiSelect
          selectProps={convertToMultiSelectProps(purchaseClassSelectProps)}
          label="ABC Class"
          queryPlaceholder="Search ABC classes"
          selectedValues={filterState?.purchaseClasses ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('purchaseClasses', values)}
          onOpenChange={onFilterOpen('purchaseClass')}
          icon={<TagOutlined />}
        />
      )}
      <AsyncMultiSelect
        selectProps={convertToMultiSelectProps(supplierSelectProps)}
        label="Primary Supplier"
        queryPlaceholder="Search suppliers"
        selectedValues={filterState?.supplierIds ?? []}
        onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('supplierIds', values)}
        onOpenChange={onFilterOpen('supplier')}
        icon={<ShopOutlined />}
      />
      {showBuyerFilter && (
        <AsyncMultiSelect
          selectProps={convertToMultiSelectProps(buyerSelectProps)}
          label="Buyer"
          queryPlaceholder="Search buyers"
          selectedValues={filterState?.buyerIds ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('buyerIds', values)}
          onOpenChange={onFilterOpen('buyer')}
          icon={<ProfileOutlined />}
        />
      )}
      {showProductGroupFilter && (
        <ProductGroupMultiSelect
          selectProps={convertToMultiSelectProps(itemGroupsSelectProps)}
          selectedValues={filterState?.itemGroupIds ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('itemGroupIds', values)}
          onOpenChange={onFilterOpen('itemGroups')}
        />
      )}
      {showItemFilter && (
        <AsyncMultiSelect
          selectProps={convertToMultiSelectProps(itemsSelectProps)}
          label="Item"
          queryPlaceholder="Search items"
          selectedValues={filterState?.itemIds ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('itemIds', values)}
          onOpenChange={onFilterOpen('items')}
          icon={<SkinOutlined />}
        />
      )}
      {showDemandPatternFilter ? (
        <AsyncMultiSelect
          selectProps={{
            // ml_demand_forecasts table uses snake_case for demand_pattern values
            options: demandPatternOptions.map((option) => ({ label: option.label, value: toSnakeCase(option.value) })),
          }}
          label="Demand Pattern"
          selectedValues={filterState?.demandPattern ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('demandPattern', values)}
          icon={<StockOutlined />}
        />
      ) : null}
      {showDemandPredictabilityFilter ? (
        <AsyncMultiSelect
          selectProps={{
            options: demandPredictabilityOptions.map((option) => ({
              label: option.label,
              value: toSnakeCase(option.value),
            })),
          }}
          label="Demand Predictability"
          selectedValues={filterState?.demandPredictability ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('demandPredictability', values)}
          icon={<MonitorOutlined />}
        />
      ) : null}
      {showRulesFilter ? (
        <AsyncMultiSelect
          selectProps={rulesSelectProps}
          label="Rules"
          queryPlaceholder="Search rules"
          disableValuesTooltip
          selectedValues={filterState?.ruleIds ?? []}
          onSelectedValuesChange={(values: string[]) => onHashStateFilterChange('ruleIds', values)}
          onOpenChange={onFilterOpen('rules')}
          icon={<SafetyCertificateOutlined />}
        />
      ) : null}

      {numberOfFiltersApplied > 0 && <ActionButton onClick={() => onFiltersChange({})} label="Clear All" />}
    </FlexSpace>
  );
};
